import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";

import { Trash2 } from "lucide-react";
import InquirySkeletonBody from "../../components/AdminComponents/inquiryForm/InquirySkeletonBody";

const InquiryForm = () => {
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/inquiries/get-all-inquiry`
      );
      // console.log("response", response.data?.inquiries);
      setInquiries(response.data?.inquiries);
      setFilteredInquiries(response?.data?.inquiries);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = [...inquiries];

    if (searchTerm) {
      filtered = filtered.filter(
        (inquiry) =>
          inquiry.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inquiry.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inquiry.phone?.includes(searchTerm)
      );
    }

    setFilteredInquiries(filtered);
    setCurrentPage(1);
  }, [searchTerm, inquiries]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentInquiries = filteredInquiries.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This inquiry will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/inquiries/delete-inquiry/${id}`
          );
          await fetchInquiries();
          if (currentInquiries.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          }

          Swal.fire({
            title: "Deleted!",
            text: "Inquiry has been deleted successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Error deleting inquiry:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete inquiry. Please try again.",
            icon: "error",
          });
        }
      }
    });
  };

  const formatDate = (dateString, includeTime = false) => {
    const date = new Date(dateString);
    const options = {
      month: "short",
      day: "numeric",
      year: "numeric",
      ...(includeTime && { hour: "2-digit", minute: "2-digit" }),
    };
    return date.toLocaleDateString("en-US", options);
  };

  return (
    <div className="min-h-screen  py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0BB6D9] mb-4">All Inquiry</h1>
        </div>

        {/* Search and Filter */}
        <div className=" mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <InquirySkeletonBody
            currentInquiries={currentInquiries}
            loading={loading}
          />
        ) : (
          <>
            {" "}
            {/* Inquiries List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentInquiries.length > 0 ? (
                currentInquiries.map((inquiry) => (
                  <div
                    key={inquiry._id}
                    className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {inquiry.firstName} {inquiry.lastName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Email:</strong> {inquiry.email || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Phone:</strong> {inquiry.phone || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Gender:</strong> {inquiry.gender || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>DOB:</strong> {inquiry?.DOB ? formatDate(inquiry.DOB) : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mb-1">
                      <strong>Speciality:</strong> {inquiry.speciality}
                    </p>
                    <p className="text-sm text-gray-700 mb-4">
                      <strong>Query:</strong> {inquiry.query || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      <strong>Created:</strong>{" "}
                      {formatDate(inquiry.createdAt, true)}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDelete(inquiry._id)}
                        className="flex-1 bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition-colors flex items-center text-center justify-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="col-span-full text-center text-gray-500 py-8">
                  No inquiries found.
                </p>
              )}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InquiryForm;
